import { useState, useCallback } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../../Keycloak/useAuthenticatedFetchWithWorkspace";

const CIVILITY_TO_TITLE: Record<string, string> = { "M.": "1", "Mme": "2", "1": "1", "2": "2" };

export interface UpdatePersonPayload {
    personId: string | number;
    reason: string;
    // Main info
    title_id?: string;
    lastname?: string;
    firstname?: string;
    birthdate?: string;
    // Coordinates
    address1?: string;
    address2?: string;
    address3_num?: string;
    address3_type?: string;
    address3_name?: string;
    address4?: string;
    zip_code?: string;
    local_city_name?: string;
    local_country_name?: string;
    email_address?: string;
    cell_phone_number?: string;
    phone_number?: string;
    // CNIL
    mailCompany?: boolean;
    emailCompany?: boolean;
    smsCompany?: boolean;
    mailTrusted?: boolean;
    emailTrusted?: boolean;
    smsTrusted?: boolean;
}

const useUpdatePerson = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState(false);

    const updatePerson = useCallback(
        async (payload: UpdatePersonPayload): Promise<boolean> => {
            if (!isReady) return false;
            setIsLoading(true);
            setError("");
            setSuccess(false);
            try {
                const body = {
                    personId: payload.personId,
                    reason: payload.reason,
                    mainInfos: {
                        title_id: CIVILITY_TO_TITLE[payload.title_id || ""] || payload.title_id || "",
                        lastname: payload.lastname || "",
                        firstname: payload.firstname || "",
                        birthdate: payload.birthdate || "",
                    },
                    coordinates: {
                        address1: payload.address1 || "",
                        address2: payload.address2 || "",
                        address3_num: payload.address3_num || "",
                        address3_type: payload.address3_type || "",
                        address3_name: payload.address3_name || "",
                        address4: payload.address4 || "",
                        zip_code: payload.zip_code || "",
                        local_city_name: payload.local_city_name || "",
                        local_country_name: payload.local_country_name || "",
                        email_address: payload.email_address || "",
                        cell_phone_number: payload.cell_phone_number || "",
                        phone_number: payload.phone_number || "",
                    },
                    cnil: {
                        mailCompanySolicitations: payload.mailCompany ? "1" : "0",
                        emailCompanySolicitations: payload.emailCompany ? "1" : "0",
                        smsCompanySolicitations: payload.smsCompany ? "1" : "0",
                        mailTrustedSolicitations: payload.mailTrusted ? "1" : "0",
                        emailTrustedSolicitations: payload.emailTrusted ? "1" : "0",
                        smsTrustedSolicitations: payload.smsTrusted ? "1" : "0",
                    },
                    emailHasChanged: 0,
                    cellphoneHasChanged: 0,
                    phoneHasChanged: 0,
                    postalAddressHasChanged: 0,
                };

                const raw = await authFetch("/customer", {
                    method: "PATCH",
                    body: JSON.stringify(body),
                });

                if (!raw || (typeof raw === "object" && "error" in (raw as object))) {
                    setError("La mise à jour a échoué. Vérifiez les informations saisies.");
                    return false;
                }
                setSuccess(true);
                return true;
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : "Une erreur est survenue");
                return false;
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, isReady]
    );

    const reset = useCallback(() => {
        setError("");
        setSuccess(false);
    }, []);

    return { updatePerson, isLoading, error, success, reset };
};

export default useUpdatePerson;
